import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useBrands } from '@/hooks/useBrands';

/**
 * Trading name a job carries, resolved exactly as the jobs_tech view does for
 * the technician app: the lead's brand, else the company default. The office
 * and the technician must never be looking at two different names, so this is
 * the only place the CRM works it out.
 *
 * The lead's own brand is taken whether or not it is still active (a renamed
 * or retired trading name is still the one that customer dealt with); the
 * fallback default must be active.
 */
export function useJobBrandName(leadId: string | null | undefined): string | null {
  const { data: brands = [] } = useBrands(true);

  const { data: leadBrand } = useQuery({
    queryKey: ['job-brand', leadId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('leads')
        .select('brands (name)')
        .eq('id', leadId!)
        .single();
      if (error) throw error;
      return (data?.brands as { name: string } | null)?.name ?? null;
    },
    enabled: !!leadId,
  });

  return leadBrand ?? brands.find(b => b.is_default)?.name ?? null;
}
